import '../i18n/autoTranslate';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../../config/api';
/* ─────────────────────────────────────────────────────────────
   CONFIG
───────────────────────────────────────────────────────────── */
const API_BASE = 'http://localhost:5000';

/* ─────────────────────────────────────────────────────────────
   INLINE UI PRIMITIVES
───────────────────────────────────────────────────────────── */
const SpotlightCard = ({ children, spotlightColor = 'rgba(16,185,129,0.35)', className = '' }) => {
  const cardRef = useRef(null);
  const handleMouseMove = useCallback((e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    cardRef.current.style.setProperty('--sx', `${e.clientX - rect.left}px`);
    cardRef.current.style.setProperty('--sy', `${e.clientY - rect.top}px`);
    cardRef.current.style.setProperty('--sc', spotlightColor);
  }, [spotlightColor]);
  return (
    <div ref={cardRef} onMouseMove={handleMouseMove}
      className={`relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] shadow-[0_20px_80px_-40px_rgba(0,0,0,0.9)] backdrop-blur-2xl ${className}`}>
      <div className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(420px circle at var(--sx,50%) var(--sy,50%), var(--sc,rgba(16,185,129,0.18)), transparent 65%)', transition: 'background 0.05s' }} />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_55%)]" />
      <div className="relative z-10">{children}</div>
    </div>
  );
};

const AppBackground = ({ children }) => (
  <div className="min-h-screen w-full bg-[#07090c] text-white relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-28 -left-28 h-[540px] w-[540px] rounded-full bg-emerald-400/10 blur-[90px]" />
      <div className="absolute -bottom-36 -right-28 h-[600px] w-[600px] rounded-full bg-sky-400/10 blur-[95px]" />
      <div className="absolute top-10 left-1/2 h-[460px] w-[460px] -translate-x-1/2 rounded-full bg-violet-400/10 blur-[110px]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.07),transparent_60%)]" />
      <div className="absolute inset-0 opacity-[0.35]"
        style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.06) 1px,transparent 1px)', backgroundSize: '64px 64px' }} />
    </div>
    <div className="relative z-10">{children}</div>
  </div>
);

/* ─────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────── */
const SKELETON_PAIRS = [
  ['nose','leftEye'],['nose','rightEye'],
  ['leftEye','leftEar'],['rightEye','rightEar'],
  ['leftShoulder','rightShoulder'],
  ['leftShoulder','leftElbow'],['leftElbow','leftWrist'],
  ['rightShoulder','rightElbow'],['rightElbow','rightWrist'],
  ['leftShoulder','leftHip'],['rightShoulder','rightHip'],
  ['leftHip','rightHip'],
  ['leftHip','leftKnee'],['leftKnee','leftAnkle'],
  ['rightHip','rightKnee'],['rightKnee','rightAnkle'],
];
const FINGER_CONNECTIONS = [
  [0,1],[1,2],[2,3],[3,4],[0,5],[5,6],[6,7],[7,8],
  [0,9],[9,10],[10,11],[11,12],[0,13],[13,14],[14,15],[15,16],
  [0,17],[17,18],[18,19],[19,20],[5,9],[9,13],[13,17],
];

/* ─────────────────────────────────────────────────────────────
   SCORE HELPERS
───────────────────────────────────────────────────────────── */
const scoreTextColor = (s) => s >= 0.7 ? 'text-emerald-400' : s >= 0.4 ? 'text-amber-400' : 'text-rose-400';
const scoreBgBorder  = (s) => s >= 0.7 ? 'bg-emerald-500/10 border-emerald-500/30' : s >= 0.4 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-rose-500/10 border-rose-500/30';
const progressFill   = (s) => s >= 0.7 ? '#10b981' : s >= 0.4 ? '#f59e0b' : '#f43f5e';
const accentFor      = (key) => ({ body_language: '#a78bfa', engagement: '#34d399', confidence: '#60a5fa', movement: '#fb923c', interview: '#f0abfc' }[key] || '#ffffff');

/* ─────────────────────────────────────────────────────────────
   METRIC CARD
───────────────────────────────────────────────────────────── */
const MetricCard = ({ label, value, accent = '#34d399', grade }) => {
  const pct = Math.round((value ?? 0) * 100);
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="pointer-events-none absolute inset-0"
        style={{ background: `radial-gradient(320px circle at 20% 10%, ${accent}26, transparent 60%)` }} />
      <div className="relative z-10 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-white/55">{label}</div>
          <div className="mt-2 flex items-baseline gap-1.5">
            <div className="text-3xl font-black tabular-nums" style={{ color: accent }}>{pct}</div>
            <div className="text-sm font-semibold text-white/60">%</div>
          </div>
          {grade && <div className="mt-1 text-[10px] font-semibold tracking-wider uppercase" style={{ color: accent + 'cc' }}>{grade}</div>}
        </div>
        <div className="h-9 w-9 rounded-xl border border-white/10 bg-white/[0.04] grid place-items-center"
          style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.03), 0 18px 60px -34px ${accent}55` }}>
          <span style={{ color: accent, fontSize: 16 }}>✦</span>
        </div>
      </div>
      <div className="relative z-10 mt-4 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: accent }} />
      </div>
    </div>
  );
};

const SparkBars = ({ values = [], accent = '#60a5fa' }) => (
  <div className="flex items-end gap-0.5 h-12">
    {values.slice(-36).map((v, i) => (
      <div key={i} className="flex-1 rounded-[3px] opacity-90"
        style={{ height: `${Math.max(6, Math.round((v ?? 0) * 48))}px`, background: accent }} />
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────
   TIP CARD
───────────────────────────────────────────────────────────── */
const TipCard = ({ tip }) => {
  const toneColors = {
    warn: { border: 'border-amber-400/30', bg: 'bg-amber-400/10', text: 'text-amber-200' },
    info: { border: 'border-sky-400/30',   bg: 'bg-sky-400/10',   text: 'text-sky-200' },
    tip:  { border: 'border-violet-400/30',bg: 'bg-violet-400/10',text: 'text-violet-200' },
  };
  const c = toneColors[tip.tone] || toneColors.info;
  return (
    <div className={`rounded-2xl border ${c.border} ${c.bg} p-3`}>
      <div className={`text-sm font-semibold ${c.text}`}>{tip.title}</div>
      <div className="mt-1 text-xs text-white/55 leading-relaxed">{tip.desc}</div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   API HELPERS
───────────────────────────────────────────────────────────── */
async function postJSON(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers:getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
  return json;
}

/* Build the payload for /video/analyze-frame from raw TF.js outputs */
function buildFramePayload({ pose, hands, faces, prevPose, frameWidth, frameHeight, frameId, timestamp }) {
  // Pose keypoints
  const kps = pose?.keypoints || [];
  const visibleKps = kps.filter(k => k.score > 0.3);
  const meanKpScore = visibleKps.length > 0
    ? visibleKps.reduce((s, k) => s + k.score, 0) / visibleKps.length
    : 0;

  const getKp = (name) => kps.find(k => k.part === name);
  const lS = getKp('leftShoulder'), rS = getKp('rightShoulder');
  const lH = getKp('leftHip'),       rH = getKp('rightHip');
  const nose = getKp('nose');

  const shoulderDiff = (lS?.score > 0.3 && rS?.score > 0.3)
    ? Math.abs(lS.position.y - rS.position.y) : null;
  const hipDiff = (lH?.score > 0.3 && rH?.score > 0.3)
    ? Math.abs(lH.position.y - rH.position.y) : null;
  const headOffset = (nose?.score > 0.3)
    ? Math.abs(nose.position.x - (frameWidth / 2)) : null;

  // Movement vs previous frame
  let avgDisplacement = 0;
  if (prevPose?.keypoints && kps.length > 0) {
    let total = 0, n = 0;
    kps.forEach((kp, i) => {
      const pk = prevPose.keypoints[i];
      if (kp.score > 0.3 && pk?.score > 0.3) {
        const dx = kp.position.x - pk.position.x;
        const dy = kp.position.y - pk.position.y;
        total += Math.sqrt(dx * dx + dy * dy);
        n++;
      }
    });
    avgDisplacement = n > 0 ? total / n : 0;
  }

  const face = faces?.[0] || null;

  return {
    frame_id:            frameId,
    timestamp:           timestamp,
    frame_width:         frameWidth,
    frame_height:        frameHeight,
    pose_detected:       visibleKps.length > 0,
    visible_keypoints:   visibleKps.length,
    total_keypoints:     17,
    mean_keypoint_score: parseFloat(meanKpScore.toFixed(4)),
    shoulder_diff_px:    shoulderDiff !== null ? parseFloat(shoulderDiff.toFixed(2)) : null,
    hip_diff_px:         hipDiff !== null ? parseFloat(hipDiff.toFixed(2)) : null,
    head_offset_px:      headOffset !== null ? parseFloat(headOffset.toFixed(2)) : null,
    avg_displacement_px: parseFloat(avgDisplacement.toFixed(2)),
    hands_detected:      hands?.length || 0,
    face_detected:       !!face,
    face_confidence:     face ? (face.probability?.[0] ?? 0.85) : null,
    face_landmarks_count: face?.landmarks?.length || 0,
  };
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const VideoAnalyzer = () => {
  /* ── refs ── */
  const videoRef    = useRef(null);
  const canvasRef   = useRef(null);
  const streamRef   = useRef(null);
  const intervalRef = useRef(null);
  const detectorRef = useRef(null);
  const handRef     = useRef(null);
  const faceRef     = useRef(null);
  const historyRef  = useRef([]);    // raw TF frame results
  const apiHistoryRef = useRef([]);  // server-enriched frame results
  const prevPoseRef = useRef(null);
  const frameNumRef = useRef(0);
  const isAnalyzingRef = useRef(false); // prevent overlapping frames
  const sessionStartRef = useRef(null);
  
  /* ── state ── */
  const [isRecording,     setIsRecording]     = useState(false);
  const [analysisData,    setAnalysisData]     = useState(null);   // latest server result
  const [temporalData,    setTemporalData]     = useState(null);   // server temporal summary
  const [sessionResult,   setSessionResult]    = useState(null);   // session end result
  const [error,           setError]            = useState(null);
  const [apiError,        setApiError]         = useState(null);
  const [isAnalyzing,     setIsAnalyzing]      = useState(false);
  const [frameCount,      setFrameCount]       = useState(0);
  const [permissionState, setPermissionState]  = useState('prompt');
  const [hasCamera,       setHasCamera]        = useState(true);
  const [showLandmarks,   setShowLandmarks]    = useState(true);
  const [analysisInterval,setAnalysisInterval] = useState(1000);
  const [modelsLoaded,    setModelsLoaded]     = useState({ pose: false, hands: false, face: false });
  const [loadingProgress, setLoadingProgress]  = useState(0);
  const [loadingError,    setLoadingError]     = useState(null);
  const [activeTab,       setActiveTab]        = useState('insights');
  const [useServerScoring,setUseServerScoring] = useState(true);  // toggle server vs client scoring
  const [apiConnected,    setApiConnected]     = useState(null);  // null=checking, true, false


  /* ─────────────────────────────────────────────
     API HEALTH CHECK
  ───────────────────────────────────────────── */
  useEffect(() => {
    fetch(`${API_BASE}/health`)
      .then(r => r.json())
      .then(() => setApiConnected(true))
      .catch(() => setApiConnected(false));
  }, []);

  /* ─────────────────────────────────────────────
     MODEL LOADING
  ───────────────────────────────────────────── */
  const loadScript = (src) => new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
    const s = document.createElement('script');
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });

  const loadModels = useCallback(async () => {
    setLoadingError(null);
    try {
      setLoadingProgress(5);
      if (!window.tf) await loadScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.2.0/dist/tf.min.js');
      await window.tf.ready();
      setLoadingProgress(20);

      if (!window.posenet) await loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/posenet@2.2.2/dist/posenet.min.js');
      detectorRef.current = await window.posenet.load({
        architecture: 'MobileNetV1', outputStride: 16,
        inputResolution: { width: 640, height: 480 }, multiplier: 0.75,
      });
      setModelsLoaded(p => ({ ...p, pose: true }));
      setLoadingProgress(50);

      if (!window.handpose) await loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/handpose@0.0.7/dist/handpose.min.js');
      handRef.current = await window.handpose.load();
      setModelsLoaded(p => ({ ...p, hands: true }));
      setLoadingProgress(75);

      if (!window.blazeface) await loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/blazeface@0.0.7/dist/blazeface.min.js');
      faceRef.current = await window.blazeface.load();
      setModelsLoaded(p => ({ ...p, face: true }));
      setLoadingProgress(100);
    } catch (err) {
      setLoadingError(`Failed to load AI models: ${err.message}`);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);

  /* ─────────────────────────────────────────────
     CAMERA PERMISSION
  ───────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      try {
        if (navigator.permissions) {
          const s = await navigator.permissions.query({ name: 'camera' });
          if (s.state === 'granted') setPermissionState('granted');
          s.onchange = () => setPermissionState(s.state);
        }
        const devs = await navigator.mediaDevices.enumerateDevices();
        const cams = devs.filter(d => d.kind === 'videoinput');
        setHasCamera(cams.length > 0);
        if (cams.length === 0) setError('No camera detected on this device.');
      } catch {}
    })();
  }, []);

  const requestPermission = useCallback(async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      stream.getTracks().forEach(t => t.stop());
      setPermissionState('granted');
    } catch {
      setPermissionState('denied');
      setError('Camera permission denied. Please allow camera access in your browser settings.');
    }
  }, []);

  /* ─────────────────────────────────────────────
     CANVAS DRAWING
  ───────────────────────────────────────────── */
  const clearCanvas = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  }, []);

  const drawLandmarks = useCallback((pose, hands, faces) => {
    const canvas = canvasRef.current, video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    const vw = video.videoWidth || 640, vh = video.videoHeight || 480;
    if (canvas.width !== vw || canvas.height !== vh) { canvas.width = vw; canvas.height = vh; }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!showLandmarks) return;

    // Pose
    if (pose?.keypoints) {
      const kpMap = Object.fromEntries(pose.keypoints.map(k => [k.part, k]));
      ctx.lineWidth = 2;
      SKELETON_PAIRS.forEach(([a, b]) => {
        const s = kpMap[a], e = kpMap[b];
        if (s?.score > 0.3 && e?.score > 0.3) {
          ctx.strokeStyle = 'rgba(0,220,100,0.7)';
          ctx.beginPath(); ctx.moveTo(s.position.x, s.position.y); ctx.lineTo(e.position.x, e.position.y); ctx.stroke();
        }
      });
      pose.keypoints.forEach(kp => {
        if (kp.score > 0.3) {
          ctx.fillStyle = '#00ff66'; ctx.beginPath();
          ctx.arc(kp.position.x, kp.position.y, 5, 0, 2 * Math.PI); ctx.fill();
        }
      });
    }

    // Hands
    if (hands?.length > 0) {
      hands.forEach((hand, hi) => {
        const color = hi === 0 ? '#ff4466' : '#ff9933';
        ctx.fillStyle = color; ctx.strokeStyle = color; ctx.lineWidth = 1.5;
        if (hand.landmarks) {
          FINGER_CONNECTIONS.forEach(([si, ei]) => {
            const s = hand.landmarks[si], e = hand.landmarks[ei];
            if (s && e) { ctx.beginPath(); ctx.moveTo(s[0], s[1]); ctx.lineTo(e[0], e[1]); ctx.stroke(); }
          });
          hand.landmarks.forEach(pt => {
            ctx.beginPath(); ctx.arc(pt[0], pt[1], 4, 0, 2 * Math.PI); ctx.fill();
          });
        }
      });
    }

    // Face
    if (faces?.length > 0) {
      faces.forEach(face => {
        if (face.topLeft && face.bottomRight) {
          const [x1, y1] = face.topLeft, [x2, y2] = face.bottomRight;
          ctx.strokeStyle = '#22d3ee'; ctx.lineWidth = 2;
          ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        }
        if (face.landmarks) {
          ctx.fillStyle = '#67e8f9';
          face.landmarks.forEach(lm => {
            ctx.beginPath(); ctx.arc(lm[0], lm[1], 3, 0, 2 * Math.PI); ctx.fill();
          });
        }
      });
    }
  }, [showLandmarks]);

  /* ─────────────────────────────────────────────
     CORE ANALYSIS FRAME
  ───────────────────────────────────────────── */
  const analyzeFrame = useCallback(async () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video?.videoHeight) return;
    if (isAnalyzingRef.current) return;
  
    isAnalyzingRef.current = true;
    setIsAnalyzing(true);
  
    try {
      const [pose, hands, faces] = await Promise.all([
        detectorRef.current
          ? detectorRef.current.estimateSinglePose(video).catch(() => null)
          : Promise.resolve(null),
        handRef.current
          ? handRef.current.estimateHands(video).catch(() => [])
          : Promise.resolve([]),
        faceRef.current
          ? faceRef.current.estimateFaces(video, false).catch(() => [])
          : Promise.resolve([]),
      ]);
  
      drawLandmarks(pose, hands, faces);
  
      const fnum = ++frameNumRef.current;
      setFrameCount(fnum);
  
      const frameWidth = video.videoWidth || 640;
      const frameHeight = video.videoHeight || 480;
  
      // Always update prevPoseRef once we have current pose
      const prevPose = prevPoseRef.current;
      prevPoseRef.current = pose;
  
      if (useServerScoring && apiConnected) {
        // ── SERVER SCORING PATH ──
        const payload = buildFramePayload({
          pose,
          hands,
          faces,
          prevPose,
          frameWidth,
          frameHeight,
          frameId: fnum,
          timestamp: Date.now(),
        });
  
        try {
          const serverResult = await postJSON("/api/video/analyze-frame", payload);
  
          // Merge server scores with face/gesture raw data for UI
          const enriched = {
            ...serverResult,
            gesture_analysis: (hands || []).reduce((acc, hand, i) => {
              acc[`hand_${i}`] = {
                confidence: hand?.handInViewConfidence ?? 0.8,
                handedness:
                  hand?.handedness ||
                  (i === 0 ? "Left" : "Right"),
                landmarks_count: hand?.landmarks?.length ?? 21,
              };
              return acc;
            }, {}),
            face_analysis:
              faces?.length > 0
                ? {
                    confidence:
                      (Array.isArray(faces[0]?.probability)
                        ? faces[0].probability?.[0]
                        : faces[0]?.probability) ?? 0.85,
                    topLeft: faces[0]?.topLeft,
                    bottomRight: faces[0]?.bottomRight,
                    landmarks: faces[0]?.landmarks ?? [],
                  }
                : null,
            posture_analysis: serverResult?.posture_analysis || {},
          };
  
          apiHistoryRef.current.push(enriched);
          if (apiHistoryRef.current.length > 60) apiHistoryRef.current.shift();
  
          setAnalysisData(enriched);
          setApiError(null);
  
          // Every 5 server frames, fetch rolling temporal summary (await to keep ordering)
          if (
            apiHistoryRef.current.length >= 5 &&
            apiHistoryRef.current.length % 5 === 0
          ) {
            try {
              const temporal = await postJSON("/api/video/temporal-summary", {
                frames: apiHistoryRef.current.slice(-20),
                window: 15,
              });
              setTemporalData(temporal);
            } catch {
              // ignore temporal summary failure
            }
          }
        } catch (err) {
          setApiError(`Server scoring failed: ${err?.message || "Unknown error"}`);
          _applyClientScoring(pose, hands, faces);
        }
      } else {
        // ── CLIENT SCORING FALLBACK ──
        _applyClientScoring(pose, hands, faces);
      }
    } catch (err) {
      console.error("Frame analysis error:", err);
    } finally {
      isAnalyzingRef.current = false;
      setIsAnalyzing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawLandmarks, useServerScoring, apiConnected]);

  /* Client-side fallback scoring (mirrors original logic) */
  const _applyClientScoring = useCallback((pose, hands, faces) => {
    const kps = pose?.keypoints || [];
    const getKp = n => kps.find(k => k.part === n);
    const lS = getKp('leftShoulder'), rS = getKp('rightShoulder');
    const lH = getKp('leftHip'),      rH = getKp('rightHip');
    const nose = getKp('nose');

    // Posture
    let posScore = 0, posCount = 0;
    const posDetails = {};
    if (lS?.score > 0.3 && rS?.score > 0.3) {
      const a = Math.max(0, 1 - Math.abs(lS.position.y - rS.position.y) / 80);
      posDetails.shoulderAlignment = a; posScore += a; posCount++;
    }
    if (lH?.score > 0.3 && rH?.score > 0.3) {
      const a = Math.max(0, 1 - Math.abs(lH.position.y - rH.position.y) / 80);
      posDetails.hipAlignment = a; posScore += a; posCount++;
    }
    if (nose?.score > 0.3) {
      const cx = canvasRef.current?.width / 2 || 320;
      const a = Math.max(0, 1 - Math.abs(nose.position.x - cx) / 180);
      posDetails.headPosition = a; posScore += a; posCount++;
    }
    const bodyLang = posCount > 0 ? Math.min(Math.max(posScore / posCount, 0), 1) : 0;

    // Movement
    let movement = 0;
    if (prevPoseRef.current?.keypoints && kps.length > 0) {
      let total = 0, n = 0;
      kps.forEach((kp, i) => {
        const pk = prevPoseRef.current.keypoints[i];
        if (kp.score > 0.3 && pk?.score > 0.3) {
          const dx = kp.position.x - pk.position.x, dy = kp.position.y - pk.position.y;
          total += Math.sqrt(dx * dx + dy * dy); n++;
        }
      });
      movement = n > 0 ? Math.min(total / n / 40, 1) : 0;
    }

    // Engagement
    let eng = 0;
    if (faces?.length > 0) eng += 0.40;
    if (hands?.length > 0) eng += 0.15 * Math.min(hands.length, 2);
    if (kps.length > 0) eng += (kps.filter(k => k.score > 0.3).length / kps.length) * 0.30;

    // Confidence (AI detection quality)
    const validKps = kps.filter(k => k.score > 0.3);
    const conf = validKps.length > 0
      ? validKps.reduce((s, k) => s + k.score, 0) / validKps.length : 0.5;

    const result = {
      timestamp: Date.now(),
      pose_detected: validKps.length > 0,
      hands_detected: hands.length,
      face_detected: faces.length > 0,
      body_language_score: bodyLang,
      confidence_score: conf,
      engagement_score: Math.min(eng, 1),
      movement_intensity: movement,
      interview_score: Math.max(0, Math.min(1, eng * 0.30 + bodyLang * 0.30 + conf * 0.25 + (1 - movement) * 0.15)),
      grades: {},
      posture_analysis: posDetails,
      gesture_analysis: (hands || []).reduce((acc, hand, i) => {
        acc[`hand_${i}`] = { confidence: hand.handInViewConfidence ?? 0.8, handedness: i === 0 ? 'Left' : 'Right', landmarks_count: hand.landmarks?.length ?? 21 };
        return acc;
      }, {}),
      face_analysis: faces.length > 0 ? {
        confidence: faces[0].probability?.[0] ?? 0.85,
        topLeft: faces[0].topLeft, bottomRight: faces[0].bottomRight, landmarks: faces[0].landmarks ?? [],
      } : null,
    };

    historyRef.current.push(result);
    if (historyRef.current.length > 60) historyRef.current.shift();
    setAnalysisData(result);

    // Client temporal
    if (frameNumRef.current % 3 === 0 && historyRef.current.length >= 3) {
      const recent = historyRef.current.slice(-10);
      const avg = f => recent.reduce((s, r) => s + (r[f] ?? 0), 0) / recent.length;
      setTemporalData({
        avg_body_language_score: avg('body_language_score'),
        avg_engagement_score:    avg('engagement_score'),
        avg_confidence_score:    avg('confidence_score'),
        avg_movement_intensity:  avg('movement_intensity'),
        num_frames: historyRef.current.length,
      });
    }
  }, []);

  /* ─────────────────────────────────────────────
     INTERVAL
  ───────────────────────────────────────────── */
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (isRecording) intervalRef.current = setInterval(analyzeFrame, analysisInterval);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRecording, analysisInterval, analyzeFrame]);

  /* ─────────────────────────────────────────────
     VIDEO START / STOP
  ───────────────────────────────────────────── */
  const resetAnalysis = useCallback(() => {
    historyRef.current = [];
    apiHistoryRef.current = [];
    prevPoseRef.current = null;
    frameNumRef.current = 0;
    setAnalysisData(null);
    setTemporalData(null);
    setSessionResult(null);
    setFrameCount(0);
    clearCanvas();
  }, [clearCanvas]);
  
  
  const startVideo = useCallback(async () => {
    resetAnalysis();
    setError(null); setSessionResult(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: false,
      });
      setPermissionState('granted');
      const v = videoRef.current;
      sessionStartRef.current = Date.now();
      
      if (v) {
        v.srcObject = stream; streamRef.current = stream;
        await v.play().catch(() => {});
        setIsRecording(true);
      }
    } catch (err) {
      setPermissionState('denied');
      setError(`Camera failed: ${err.message}`);
    }
  }, [resetAnalysis]);

  const stopVideo = useCallback(async () => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    clearCanvas();
    setIsRecording(false);
    setFrameCount(0);
    frameNumRef.current = 0;

    // On stop: request full session analysis from server
    const sessionFrames = apiHistoryRef.current.length > 0
      ? apiHistoryRef.current : historyRef.current;
    if (sessionFrames.length >= 3 && apiConnected) {
      const durationSec = sessionStartRef.current
          ? (Date.now() - sessionStartRef.current) / 1000
          : null;
        
        postJSON('/api/video/analyze-session', {
          frames: sessionFrames,
          session_duration_seconds: durationSec
        })
        .then(r => { setSessionResult(r); setActiveTab('session'); })
        .catch(() => {});
    }
  }, [clearCanvas, apiConnected]);

  useEffect(() => () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

 

  /* ─────────────────────────────────────────────
     DERIVED / TREND DATA
  ───────────────────────────────────────────── */
  const allModelsLoaded = Object.values(modelsLoaded).every(Boolean);
  const canStart = hasCamera && allModelsLoaded;
  const frameHistory = apiHistoryRef.current.length > 0 ? apiHistoryRef.current : historyRef.current;
  const recent = frameHistory.slice(-36);
  const trendEngagement = recent.map(r => r.engagement_score ?? 0);
  const trendConfidence = recent.map(r => r.confidence_score ?? 0);
  const trendBody       = recent.map(r => r.body_language_score ?? 0);
  const trendMovement   = recent.map(r => r.movement_intensity ?? 0);

  /* Coaching tips — prefer server-provided, fallback to client-derived */
  const tips = analysisData?.coaching_tips ?? (() => {
    const d = analysisData;
    if (!d) return [];
    const out = [];
    if (!d.face_detected) out.push({ tone: 'warn', title: 'Keep your face in frame', desc: 'Center yourself and keep your eyes near the camera.' });
    if ((d.body_language_score ?? 0) < 0.55) out.push({ tone: 'info', title: 'Stronger posture reads as confidence', desc: 'Square your shoulders, sit upright.' });
    if ((d.engagement_score ?? 0) < 0.55) out.push({ tone: 'info', title: 'Increase engagement cues', desc: 'Add small nods, keep steady eye line.' });
    if ((d.movement_intensity ?? 0) > 0.72) out.push({ tone: 'warn', title: 'Reduce fidgeting', desc: 'Keep gestures intentional.' });
    if ((d.hands_detected ?? 0) === 0) out.push({ tone: 'tip', title: 'Use visible gestures', desc: 'Natural hand gestures improve perceived confidence.' });
    return out.slice(0, 3);
  })();

  const tabs = [
    { id: 'insights', label: 'Live' },
    { id: 'trends',   label: 'Trends' },
    { id: 'session',  label: 'Session', badge: sessionResult ? '✓' : null },
    { id: 'models',   label: 'Models' },
  ];

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <AppBackground>
      <div className="px-4 py-6 md:px-8 max-w-7xl mx-auto space-y-5">

        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl p-6 md:p-8">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_55%)]" />
          <div className="relative">
            <div className="inline-flex flex-wrap items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70">
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.65)]" />
              Video Interview Analyzer • Live body language • On-device AI
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px]">Privacy-first</span>
              <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px]">Real-time</span>
              {/* API status pill */}
              <span className={`rounded-full px-2 py-0.5 text-[10px] border ${
                apiConnected === null ? 'border-white/10 bg-white/5 text-white/40' :
                apiConnected ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' :
                'border-amber-500/30 bg-amber-500/10 text-amber-300'
              }`}>
                {apiConnected === null ? '⟳ API' : apiConnected ? '✓ API Connected' : '⚠ API Offline'}
              </span>
            </div>

            <h1 className="mt-5 text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.05] text-white">
              Practice your video interview with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-200 via-sky-200 to-emerald-200">
                instant non-verbal feedback
              </span>.
            </h1>
            <p className="mt-3 text-sm sm:text-base text-white/60 max-w-3xl">
              Live pose, hands, and face signals (TensorFlow.js) → scored server-side via Flask NLP API for precise posture, engagement, confidence, and movement insights.
            </p>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
              <SpotlightCard spotlightColor="rgba(99,102,241,0.35)">
                <div className="p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <button onClick={isRecording ? stopVideo : startVideo} disabled={!canStart}
                      className="group relative overflow-hidden inline-flex items-center gap-2 px-6 py-2.5 rounded-2xl font-semibold text-sm border border-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: isRecording ? 'linear-gradient(135deg,rgba(244,63,94,0.85),rgba(159,18,57,0.85))' : 'linear-gradient(135deg,rgba(99,102,241,0.85),rgba(124,58,237,0.85))' }}>
                      <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.22),transparent_55%)]" />
                      <span className="relative flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${isRecording ? 'bg-white animate-pulse' : 'bg-emerald-300'}`} />
                        {isRecording ? 'Stop Session' : 'Start Live Session'}
                      </span>
                    </button>

                    <button onClick={resetAnalysis} disabled={!analysisData && frameCount === 0}
                      className="px-4 py-2.5 rounded-2xl font-semibold text-sm bg-white/[0.04] hover:bg-white/[0.06] disabled:opacity-30 disabled:cursor-not-allowed transition border border-white/10">
                      Reset
                    </button>

                    <button onClick={() => setShowLandmarks(v => !v)}
                      className="px-4 py-2.5 rounded-2xl font-semibold text-sm transition border border-white/10 bg-white/[0.04] hover:bg-white/[0.06]"
                      style={{ boxShadow: showLandmarks ? '0 0 0 1px rgba(16,185,129,0.25)' : 'none' }}>
                      {showLandmarks ? 'Landmarks: ON' : 'Landmarks: OFF'}
                    </button>

                    {/* Server scoring toggle */}
                    <button onClick={() => setUseServerScoring(v => !v)}
                      className="px-4 py-2.5 rounded-2xl font-semibold text-sm transition border border-white/10 bg-white/[0.04] hover:bg-white/[0.06]"
                      style={{ boxShadow: useServerScoring && apiConnected ? '0 0 0 1px rgba(99,102,241,0.35)' : 'none' }}>
                      {useServerScoring ? '🖥 Server Scoring' : '💻 Client Scoring'}
                    </button>

                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-xs text-white/45">Interval</span>
                      <select value={analysisInterval} onChange={e => setAnalysisInterval(Number(e.target.value))}
                        className="px-3 py-2 rounded-2xl text-sm font-medium bg-white/[0.04] border border-white/10 text-white/80 cursor-pointer">
                        <option value={500}>Fast (0.5s)</option>
                        <option value={1000}>Balanced (1s)</option>
                        <option value={2000}>Light (2s)</option>
                      </select>
                    </div>
                  </div>

                  {/* Status row */}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-white/55">
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
                      <span className={`h-2 w-2 rounded-full ${isRecording ? 'bg-emerald-400 animate-pulse' : 'bg-white/25'}`} />
                      {isRecording ? 'Live' : 'Idle'}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
                      Frames <span className="font-mono text-white/80">{frameCount}</span>
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">
                      Models <span className="font-mono text-white/80">{Object.values(modelsLoaded).filter(Boolean).length}/3</span>
                    </span>
                    {isAnalyzing && (
                      <span className="inline-flex items-center gap-2 rounded-full border border-indigo-400/25 bg-indigo-400/10 px-3 py-1 text-indigo-200">
                        <span className="w-3 h-3 rounded-full border border-indigo-300 border-t-transparent animate-spin inline-block" />
                        Analyzing…
                      </span>
                    )}
                    {apiError && (
                      <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/25 bg-amber-400/10 px-3 py-1 text-amber-200">
                        ⚠ {apiError}
                      </span>
                    )}
                    <span className="ml-auto text-white/45">
                      {useServerScoring && apiConnected ? 'Scoring via Flask API' : 'Scoring in browser'}
                    </span>
                  </div>
                </div>
              </SpotlightCard>

              <div className="hidden lg:block">
                <div className="h-full rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl p-4 flex flex-col justify-between">
                  <div className="text-xs text-white/55 tracking-wide">Recommended interview type</div>
                  <div className="mt-2 text-sm font-semibold text-white">Structured async (per-question timer)</div>
                  <div className="mt-1 text-xs text-white/50">Best for fairness + clear feedback loops. Add prep time → record answer → instant coaching.</div>
                  <div className="mt-3 inline-flex items-center gap-2 text-[11px] text-white/55">
                    {['Prep','Answer','Review'].map(s => (
                      <span key={s} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Model loading progress */}
        {!allModelsLoaded && !loadingError && (
          <SpotlightCard spotlightColor="rgba(99,102,241,0.4)">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-5 h-5 rounded-full border-2 border-indigo-300/70 border-t-transparent animate-spin" />
                <span className="font-semibold text-indigo-200 tracking-wide">Loading AI Models</span>
                <span className="ml-auto text-indigo-200/80 font-mono text-sm">{loadingProgress}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-4">
                <div className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${loadingProgress}%`, background: 'linear-gradient(90deg,#6366f1,#8b5cf6,#06b6d4)' }} />
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                {[{ key:'pose',label:'PoseNet',color:'#60a5fa'},{ key:'hands',label:'HandPose',color:'#fb7185'},{ key:'face',label:'BlazeFace',color:'#34d399'}].map(({ key, label, color }) => (
                  <div key={key} className="flex items-center gap-2 rounded-2xl p-3 border border-white/10 bg-white/[0.03]"
                    style={{ boxShadow: modelsLoaded[key] ? `0 0 0 1px ${color}26` : '0 0 0 1px rgba(255,255,255,0.03)' }}>
                    <div className="w-2 h-2 rounded-full" style={{ background: modelsLoaded[key] ? color : 'rgba(255,255,255,0.25)' }} />
                    <span className="text-white/70">{label}</span>
                    <span className="ml-auto text-xs" style={{ color: modelsLoaded[key] ? color : 'rgba(255,255,255,0.35)' }}>
                      {modelsLoaded[key] ? '✓' : '…'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </SpotlightCard>
        )}

        {/* Errors */}
        {loadingError && (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 flex gap-3 items-start">
            <span className="text-rose-300 text-lg">⚠</span>
            <div>
              <div className="font-semibold text-rose-200 mb-1">Model Load Failed</div>
              <div className="text-rose-200/70 text-sm">{loadingError}</div>
              <button onClick={loadModels} className="mt-3 px-4 py-2 rounded-xl text-sm font-semibold bg-rose-600/80 hover:bg-rose-600 transition">Retry</button>
            </div>
          </div>
        )}
        {error && (
          <div className="rounded-2xl border border-rose-500/25 bg-rose-500/10 p-4 flex items-center gap-3">
            <span className="text-rose-300">⚠</span>
            <span className="text-rose-100/80 text-sm">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-rose-200/70 hover:text-rose-200 text-lg">×</button>
          </div>
        )}

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Live feed */}
          <SpotlightCard spotlightColor="rgba(56,189,248,0.30)">
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-white/85 flex items-center gap-2">
                  <span className="text-white/50">📷</span> Live Camera
                </h2>
                <span className="text-[11px] text-white/45">Overlay: Pose · Hands · Face</span>
              </div>

              {permissionState !== 'granted' ? (
                <div className="rounded-2xl bg-amber-500/10 border border-amber-500/25 p-6 text-center">
                  <div className="text-3xl mb-3">🎥</div>
                  <p className="text-amber-100 font-semibold mb-1">Camera access required</p>
                  <p className="text-white/55 text-sm mb-4">All processing is local. Nothing is uploaded.</p>
                  <button onClick={requestPermission}
                    className="px-6 py-2.5 rounded-2xl font-semibold text-sm border border-amber-200/20 bg-amber-500/20 hover:bg-amber-500/25 transition">
                    Allow Camera
                  </button>
                </div>
              ) : !hasCamera ? (
                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-10 text-center text-white/60">No camera detected</div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10" style={{ minHeight: 260 }}>
                  <video ref={videoRef} autoPlay muted playsInline
                    onLoadedMetadata={() => {
                      const v = videoRef.current, c = canvasRef.current;
                      if (v && c) { c.width = v.videoWidth; c.height = v.videoHeight; }
                    }}
                    className="w-full h-auto block" style={{ maxHeight: 420 }} />
                  <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" style={{ imageRendering: 'pixelated' }} />

                  {/* Score overlay — server interview_score */}
                  {analysisData?.interview_score != null && isRecording && (
                    <div className="absolute top-3 left-3 rounded-xl border border-white/10 bg-black/60 backdrop-blur px-3 py-2">
                      <div className="text-[10px] text-white/50 uppercase tracking-wider">Interview Score</div>
                      <div className="text-xl font-black tabular-nums" style={{ color: accentFor('interview') }}>
                        {Math.round(analysisData.interview_score * 100)}%
                      </div>
                    </div>
                  )}

                  {isRecording ? (
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-rose-500/90 text-white px-2.5 py-1 rounded-full text-xs font-semibold border border-white/10">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse" /> LIVE
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                      <span className="text-white/60 text-sm">Press "Start Live Session" to begin</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </SpotlightCard>

          {/* Insights panel */}
          <SpotlightCard spotlightColor="rgba(16,185,129,0.28)">
            <div className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-sm font-semibold text-white/85 flex items-center gap-2">
                  <span className="text-white/50">🧠</span> Insights
                </h2>
                <div className="ml-auto inline-flex items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-1">
                  {tabs.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)}
                      className="px-3 py-1.5 rounded-xl text-xs font-semibold transition relative"
                      style={{
                        background: activeTab === t.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                        color: activeTab === t.id ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.55)',
                        border: activeTab === t.id ? '1px solid rgba(255,255,255,0.10)' : '1px solid transparent',
                      }}>
                      {t.label}
                      {t.badge && <span className="ml-1 text-emerald-400">{t.badge}</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── LIVE TAB ── */}
              {activeTab === 'insights' && (
                analysisData ? (
                  <div className="space-y-4">
                    {/* Score grid — server scores take priority */}
                    <div className="grid grid-cols-2 gap-3">
                      <MetricCard label="Body Language" value={analysisData.body_language_score} accent="#a78bfa" grade={analysisData.grades?.body_language} />
                      <MetricCard label="Engagement"    value={analysisData.engagement_score}    accent="#34d399" grade={analysisData.grades?.engagement} />
                      <MetricCard label="Confidence"    value={analysisData.confidence_score}    accent="#60a5fa" grade={analysisData.grades?.confidence} />
                      <MetricCard label="Movement"      value={analysisData.movement_intensity}  accent="#fb923c" />
                    </div>

                    {/* Interview score bar — server only */}
                    {analysisData.interview_score != null && (
                      <div className="rounded-2xl border border-fuchsia-400/25 bg-fuchsia-400/10 p-3">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-[11px] text-fuchsia-200 font-semibold tracking-[0.22em] uppercase">Interview Readiness</span>
                          <span className="text-fuchsia-200 font-black text-lg tabular-nums">
                            {Math.round(analysisData.interview_score * 100)}%
                            {analysisData.grades?.interview && <span className="text-xs ml-1 text-fuchsia-300/70 font-medium">{analysisData.grades.interview}</span>}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.round(analysisData.interview_score * 100)}%`, background: 'linear-gradient(90deg,#d946ef,#a78bfa)' }} />
                        </div>
                      </div>
                    )}

                    {/* Detection status */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                      <div className="text-[11px] text-white/55 font-semibold tracking-[0.22em] uppercase mb-2">Detection Status</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: 'Pose',  active: analysisData.pose_detected,     count: null },
                          { label: 'Hands', active: analysisData.hands_detected > 0, count: analysisData.hands_detected },
                          { label: 'Face',  active: analysisData.face_detected,      count: null },
                        ].map(({ label, active, count }) => (
                          <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-center"
                            style={{ boxShadow: active ? '0 0 0 1px rgba(16,185,129,0.22)' : '0 0 0 1px rgba(244,63,94,0.18)' }}>
                            <div className="mx-auto w-2.5 h-2.5 rounded-full" style={{ background: active ? '#10b981' : '#f43f5e' }} />
                            <div className="mt-2 text-xs font-semibold" style={{ color: active ? '#a7f3d0' : '#fecaca' }}>
                              {label}{count !== null ? ` (${count})` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Posture breakdown */}
                    {analysisData.posture_analysis && Object.keys(analysisData.posture_analysis).length > 0 && (
                      <div className="rounded-2xl border border-violet-400/25 bg-violet-400/10 p-3">
                        <div className="text-[11px] text-violet-200 font-semibold tracking-[0.22em] uppercase mb-2">Posture Breakdown</div>
                        <div className="space-y-2">
                          {[
                            { key: 'shoulderAlignment', label: 'Shoulders' },
                            { key: 'hipAlignment',      label: 'Hips' },
                            { key: 'headPosition',      label: 'Head Center' },
                          ].map(({ key, label }) => {
                            const v = analysisData.posture_analysis[key];
                            if (v === undefined) return null;
                            const pct = Math.round(v * 100);
                            return (
                              <div key={key}>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="text-white/60">{label}</span>
                                  <span className={scoreTextColor(v)} style={{ fontVariantNumeric: 'tabular-nums' }}>{pct}%</span>
                                </div>
                                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: progressFill(v) }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Hands */}
                    {analysisData.gesture_analysis && Object.keys(analysisData.gesture_analysis).length > 0 && (
                      <div className="rounded-2xl border border-orange-400/25 bg-orange-400/10 p-3">
                        <div className="text-[11px] text-orange-200 font-semibold tracking-[0.22em] uppercase mb-2">Hands</div>
                        <div className="space-y-2">
                          {Object.entries(analysisData.gesture_analysis).map(([key, data]) => (
                            <div key={key} className="flex items-center justify-between text-sm">
                              <span className="text-white/60">{data.handedness} Hand</span>
                              <div className="flex items-center gap-3">
                                <span className="text-white/45 text-xs">{data.landmarks_count} pts</span>
                                <span className="text-orange-200 font-semibold">{(data.confidence * 100).toFixed(0)}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Face */}
                    {analysisData.face_analysis && (
                      <div className="rounded-2xl border border-cyan-400/25 bg-cyan-400/10 p-3">
                        <div className="text-[11px] text-cyan-100 font-semibold tracking-[0.22em] uppercase mb-2">Face</div>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-white/60">Detection Confidence</span>
                            <span className="text-cyan-100 font-semibold">{(analysisData.face_analysis.confidence * 100).toFixed(0)}%</span>
                          </div>
                          {analysisData.face_analysis.landmarks?.length > 0 && (
                            <div className="flex justify-between">
                              <span className="text-white/60">Landmarks</span>
                              <span className="text-white/70 text-xs">{analysisData.face_analysis.landmarks.length} pts</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Coaching tips */}
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-[11px] text-white/55 font-semibold tracking-[0.22em] uppercase">Coaching</div>
                        <div className="text-[11px] text-white/45">Top {Math.min(tips.length, 3)}</div>
                      </div>
                      {tips.length ? (
                        <div className="space-y-2">{tips.slice(0,3).map((t, i) => <TipCard key={t.id || i} tip={t} />)}</div>
                      ) : (
                        <div className="text-sm text-emerald-300/80">✓ Looking great — keep it up!</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-14 text-white/55">
                    <div className="text-5xl mb-3 opacity-30">👤</div>
                    <p className="font-semibold text-white/70">Start a session to see live analysis</p>
                    <p className="text-sm mt-1">Posture · Engagement · Confidence · Movement</p>
                  </div>
                )
              )}

              {/* ── TRENDS TAB ── */}
              {activeTab === 'trends' && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="text-[11px] text-white/55 font-semibold tracking-[0.22em] uppercase mb-3">Last {recent.length} frames</div>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { label: 'Engagement', values: trendEngagement, accent: '#34d399' },
                        { label: 'Confidence', values: trendConfidence, accent: '#60a5fa' },
                        { label: 'Body Language', values: trendBody, accent: '#a78bfa' },
                        { label: 'Movement', values: trendMovement, accent: '#fb923c' },
                      ].map(({ label, values, accent }) => (
                        <div key={label}>
                          <div className="flex items-center justify-between text-xs text-white/55 mb-2">
                            <span>{label}</span>
                            <span className="text-white/40 tabular-nums">{values.length > 0 ? `${Math.round(values[values.length-1] * 100)}%` : '—'}</span>
                          </div>
                          <SparkBars values={values} accent={accent} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {temporalData && (
                    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] text-white/55 font-semibold tracking-[0.22em] uppercase">
                          {temporalData.window_size ? 'Rolling Averages' : 'Temporal Averages'}
                        </div>
                        <div className="text-xs text-white/45">
                          {temporalData.num_frames || temporalData.window_size} frames
                          {temporalData.grade && <span className="ml-2 text-emerald-300">{temporalData.grade}</span>}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                          { label: 'Body',    value: temporalData.avg_body_language_score, accent: '#a78bfa', stab: temporalData.stability_body_language_score },
                          { label: 'Engage',  value: temporalData.avg_engagement_score,    accent: '#34d399', stab: temporalData.stability_engagement_score },
                          { label: 'Conf',    value: temporalData.avg_confidence_score,    accent: '#60a5fa', stab: temporalData.stability_confidence_score },
                          { label: 'Move',    value: temporalData.avg_movement_intensity,  accent: '#fb923c', stab: temporalData.stability_movement_intensity },
                        ].map(m => (
                          <div key={m.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                            <div className="text-xs text-white/55">{m.label}</div>
                            <div className="mt-2 text-2xl font-black tabular-nums" style={{ color: m.accent }}>
                              {((m.value ?? 0) * 100).toFixed(1)}<span className="text-sm font-semibold text-white/50">%</span>
                            </div>
                            {m.stab != null && (
                              <div className="text-[10px] text-white/35 mt-1">Stability: {Math.round(m.stab * 100)}%</div>
                            )}
                          </div>
                        ))}
                      </div>
                      {temporalData.avg_interview_score != null && (
                        <div className="mt-3 p-3 rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/8">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-fuchsia-200/70">Avg Interview Score</span>
                            <span className="text-fuchsia-200 font-black text-lg">{Math.round(temporalData.avg_interview_score * 100)}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── SESSION TAB ── */}
              {activeTab === 'session' && (
                <div className="space-y-4">
                  {sessionResult ? (
                    <>
                      <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-4">
                        <div className="text-[11px] text-emerald-200 font-semibold tracking-[0.22em] uppercase mb-2">Session Complete</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-xs text-white/55">Interview Score</div>
                            <div className="text-3xl font-black tabular-nums text-fuchsia-300">
                              {Math.round((sessionResult.summary?.avg_interview_score ?? 0) * 100)}%
                            </div>
                            <div className="text-sm text-white/55">{sessionResult.summary?.grade}</div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {[
                              { label: 'Frames', value: sessionResult.frame_count },
                              { label: 'Face %', value: `${Math.round((sessionResult.summary?.face_presence_rate ?? 0) * 100)}%` },
                              { label: 'Pose %', value: `${Math.round((sessionResult.summary?.pose_presence_rate ?? 0) * 100)}%` },
                              { label: 'Dur', value: `${sessionResult.summary?.duration_estimate_seconds ?? '—'}s` },
                            ].map(({ label, value }) => (
                              <div key={label} className="rounded-xl border border-white/10 bg-white/[0.03] p-2 text-center">
                                <div className="text-[10px] text-white/40 uppercase">{label}</div>
                                <div className="text-sm font-bold text-white/80 mt-0.5">{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {sessionResult.coaching_tips?.length > 0 && (
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
                          <div className="text-[11px] text-white/55 font-semibold tracking-[0.22em] uppercase mb-3">Session Coaching</div>
                          <div className="space-y-2">
                            {sessionResult.coaching_tips.map((t, i) => <TipCard key={t.id || i} tip={t} />)}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-14 text-white/45">
                      <div className="text-4xl mb-3 opacity-30">📊</div>
                      <p className="font-semibold text-white/60">No session data yet</p>
                      <p className="text-sm mt-1">Stop a live session to see your full report</p>
                    </div>
                  )}
                </div>
              )}

              {/* ── MODELS TAB ── */}
              {activeTab === 'models' && (
                <div className="space-y-4">
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="text-[11px] text-white/55 font-semibold tracking-[0.22em] uppercase mb-3">TensorFlow.js models</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { key:'pose',  label:'PoseNet',   desc:'17 body keypoints',  accent:'#60a5fa', version:'v2.2.2' },
                        { key:'hands', label:'HandPose',  desc:'21 hand landmarks',  accent:'#fb7185', version:'v0.0.7' },
                        { key:'face',  label:'BlazeFace', desc:'6 facial landmarks', accent:'#34d399', version:'v0.0.7' },
                      ].map(({ key, label, desc, accent, version }) => (
                        <div key={key} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold" style={{ color: accent }}>{label}</span>
                            <span className="text-[11px] px-2 py-0.5 rounded-full font-mono border border-white/10"
                              style={{ background: modelsLoaded[key] ? `${accent}22` : 'rgba(255,255,255,0.04)', color: modelsLoaded[key] ? accent : 'rgba(255,255,255,0.45)' }}>
                              {modelsLoaded[key] ? '✓ Active' : 'Loading…'}
                            </span>
                          </div>
                          <div className="text-xs text-white/55">{desc}</div>
                          <div className="text-xs text-white/35 mt-1">{version}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Flask API status */}
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                    <div className="text-[11px] text-white/55 font-semibold tracking-[0.22em] uppercase mb-3">Flask API</div>
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${apiConnected ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                      <span className="text-sm text-white/70">{API_BASE}</span>
                      <span className={`ml-auto text-xs px-2 py-1 rounded-lg border ${apiConnected ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10' : 'border-rose-500/30 text-rose-300 bg-rose-500/10'}`}>
                        {apiConnected ? 'Connected' : 'Offline'}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-white/40">
                      Routes: /video/analyze-frame · /video/analyze-session · /video/temporal-summary · /video/coaching-tips
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/55 space-y-1.5">
                    <p><span className="font-semibold text-white/75">Privacy:</span> TF.js inference is local. Only landmark metadata (numbers, no video) is sent to the Flask API for scoring.</p>
                    <p><span className="font-semibold text-white/75">Fallback:</span> If the API is offline, client-side scoring activates automatically.</p>
                  </div>
                </div>
              )}
            </div>
          </SpotlightCard>
        </div>
      </div>
    </AppBackground>
  );
};

export default VideoAnalyzer;
